
import { NoseurObject } from "@rivtn/noseur";
import { BaseService, SanitizedResponse } from "../BaseService";

export type IFeatureMappingValue = number | boolean | "Unlimited";

export interface IPlatformPlan {
    name: string;
    code: string;
    rgb_csv: string;
    enabled: boolean;
    can_bill_yearly: boolean;
    can_bill_monthly: boolean;
    reason_to_upgrade: NoseurObject<string>;
    yearly_based_discount_percentage: number;
    monthly_based_discount_percentage: number;
    yearly_based_amounts: NoseurObject<number>;
    monthly_based_amounts: NoseurObject<number>;
    yearly_based_discounts: NoseurObject<number>;
    monthly_based_discounts: NoseurObject<number>;
    previous_yearly_based_amounts: NoseurObject<number>;
    previous_monthly_based_amounts: NoseurObject<number>;
    features_mapping: NoseurObject<IFeatureMappingValue>;
    __calculated_amounts: {
        yearly: {
            amount: number;
            percentageOff: number;
            durationIsMonthly: boolean;
            amountBilledMonthly: number;
        };
        monthly: {
            amount: number;
            percentageOff: number;
            durationIsMonthly: boolean;
            amountBilledMonthly: number;
        };
    };
}

export interface IPlatformFeature {
    code: string;
    icon: string;
    label: string;
    display: boolean;
}

export interface IPlatformFeatureGroup {
    code: string;
    label: string;
    display: boolean;
    icon_url: string;
    features: IPlatformFeature[];
}

export class PlatformService extends BaseService {

    protected static instance: PlatformService;

    private constructor() {
        super();
    }

    static getInstance() {
        if (!PlatformService.instance) PlatformService.instance = new PlatformService();
        return PlatformService.instance;
    }

    queryPlans(currencyCode?: string, params: NoseurObject<string | number | boolean> = { page: 1, size: 10, enabled: true, sort: "created_at,asc" }): Promise<SanitizedResponse<IPlatformPlan>> {
        return this.report(this.transport.get("/v1/platform/plan", { query: this.normalizeParams(params) }), ({ apiResponse }: SanitizedResponse<IPlatformPlan>) => {
            apiResponse.data!.content = apiResponse.data!.content?.map((plan) => {
                if (currencyCode) plan.__calculated_amounts = {
                    yearly: this.calculateAndReturnPlanAmounts(currencyCode, plan, false),
                    monthly: this.calculateAndReturnPlanAmounts(currencyCode, plan, true),
                };
                return plan;
            });
        });
    }

    queryFeatures(params: NoseurObject<string | number | boolean> = { page: 1, size: 10, display: true, sort: "created_at,asc" }): Promise<SanitizedResponse<IPlatformFeatureGroup>> {
        return this.report(this.transport.get("/v1/platform/feature", { query: this.normalizeParams(params) }));
    }

    // utils
    calculateAndReturnPlanAmounts(currencyCode: string, plan: IPlatformPlan, durationIsMonthly: boolean) {
        const discountPercentage = (durationIsMonthly ? plan.monthly_based_discount_percentage : plan.yearly_based_discount_percentage) ?? 0;
        let ogAmount = (durationIsMonthly ? plan.monthly_based_amounts : plan.yearly_based_amounts)[currencyCode] ?? 0;
        const discount = (durationIsMonthly ? plan.monthly_based_discounts : plan.yearly_based_discounts)[currencyCode] ?? 0;
        let amount = ogAmount - discount;
        if (discountPercentage > 0) {
            amount -= (amount * discountPercentage) / 100;
        }
        amount = Math.max(amount, 0);
        const amountBilledMonthly = (durationIsMonthly ? amount : (amount / 12));
        const percentageOff = (amount === ogAmount ? NaN : ((ogAmount - amount) * 100) / ogAmount);
        return {
            amount,
            percentageOff,
            durationIsMonthly,
            amountBilledMonthly,
        }
    }

}
